import React, { useState, useMemo } from 'react';
import { Button, Table, Progress, message, Card, Spin, Modal, Select, Tag, Input, Row, Col, Space } from 'antd';
import { DownloadOutlined, SearchOutlined, FilterOutlined, ClearOutlined } from '@ant-design/icons';
import AxiosOICStat from "AxiosOICStat";
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { writeExcelFile } from '../../utils/excelExport';
import { getIndicatorName } from '../../utils/indicatorName';

const { Search } = Input;

export const SdgComparisonTab = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [progress, setProgress] = useState(0);
    const [estimatedComparisons, setEstimatedComparisons] = useState(0);
    const [sdgRecords, setSdgRecords] = useState(0);
    const [oicRecords, setOicRecords] = useState(0);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [goal, setGoal] = useState(1);
    const countriesState = useSelector((state) => state.countries);
    const indicatorsState = useSelector((state) => state.indicators);
    
    const [filters, setFilters] = useState({
        indicator: null,
        country: null,
        year: null,
        match: null,
        seriesCode: null,
    });

    const columns = [
        {
            title: 'Indicator Code',
            dataIndex: 'ind_code',
            key: 'ind_code',
            sorter: (a, b) => a.ind_code - b.ind_code,
            width: 120,
        },
        {
            title: 'Indicator Name',
            dataIndex: 'ind_code',
            key: 'indicator_name',
            render: (indCode) => {
                const indicator = indicatorsState.value ? indicatorsState.value[indCode] : null;
                return indicator ? getIndicatorName(indicator) : indCode;
            },
            sorter: (a, b) => {
                const aName = indicatorsState.value && indicatorsState.value[a.ind_code] ? getIndicatorName(indicatorsState.value[a.ind_code]) : '';
                const bName = indicatorsState.value && indicatorsState.value[b.ind_code] ? getIndicatorName(indicatorsState.value[b.ind_code]) : '';
                return aName.localeCompare(bName);
            },
            width: 300,
        },
        {
            title: 'Private/Public',
            dataIndex: 'ind_code',
            key: 'private_public',
            render: (indCode) => {
                const indicator = indicatorsState.value ? indicatorsState.value[indCode] : null;
                return indicator ? (indicator.is_private === 1 ? 'Private' : 'Public') : 'Unknown';
            },
            filters: [
                { text: 'Public', value: 'public' },
                { text: 'Private', value: 'private' },
            ],
            onFilter: (value, record) => {
                const indicator = indicatorsState.value ? indicatorsState.value[record.ind_code] : null;
                if (value === 'public') {
                    return indicator && indicator.is_private !== 1;
                } else if (value === 'private') {
                    return indicator && indicator.is_private === 1;
                }
                return false;
            },
            width: 40,
        },
        {
            title: 'Country Code',
            dataIndex: 'c_code',
            key: 'c_code',
            sorter: (a, b) => a.c_code - b.c_code,
            width: 120,
        },
        {
            title: 'Country Name',
            dataIndex: 'country_name',
            key: 'country_name',
            sorter: (a, b) => (a.country_name || '').localeCompare(b.country_name || ''),
            width: 200,
        },
        {
            title: 'Series Code',
            dataIndex: 'series_code',
            key: 'series_code',
            sorter: (a, b) => a.series_code.localeCompare(b.series_code),
            width: 150,
        },
        {
            title: 'Geo Area Code',
            dataIndex: 'geo_area_code',
            key: 'geo_area_code',
            sorter: (a, b) => (a.geo_area_code || '').localeCompare(b.geo_area_code || ''),
            width: 120,
        },
        {
            title: 'Year',
            dataIndex: 'year',
            key: 'year',
            sorter: (a, b) => a.year - b.year,
            width: 80,
        },
        {
            title: 'SDG Value',
            dataIndex: 'sdg_value',
            key: 'sdg_value',
            sorter: (a, b) => {
                const aVal = parseFloat(a.sdg_value) || 0;
                const bVal = parseFloat(b.sdg_value) || 0;
                return aVal - bVal;
            },
            width: 120,
        },
        {
            title: 'OIC Value',
            dataIndex: 'oic_value',
            key: 'oic_value',
            sorter: (a, b) => {
                const aVal = parseFloat(a.oic_value) || 0;
                const bVal = parseFloat(b.oic_value) || 0;
                return aVal - bVal;
            },
            width: 120,
        },
        {
            title: 'Match',
            dataIndex: 'match',
            key: 'match',
            render: (match) => (
                <Tag color={match ? 'green' : 'red'}>
                    {match ? 'Match' : 'No Match'}
                </Tag>
            ),
            filters: [
                { text: 'Match', value: true },
                { text: 'No Match', value: false },
            ],
            onFilter: (value, record) => record.match === value,
            width: 60,
        },
    ];

    const exportToCsv = () => {
        if (data.length === 0) {
            message.warning('No data available to export');
            return;
        }
    
        try {
            const nonMatchedData = data.filter(r => !r.match);
            
            if (nonMatchedData.length === 0) {
                message.info('No non-matching data found to export');
                // return;
            }
            
            const allCountries = Object.values(countriesState.value || {}).map(c => c.c_code).sort((a, b) => a - b);
            const indicators = indicatorsState.value || {};
        
            const groupedDataSdg = {};
            const groupedDataOic = {};
            
            data.forEach(r => {
                const key = `${r.ind_code}_${r.c_code}`;
                        
                if (!groupedDataSdg[key]) {
                    groupedDataSdg[key] = { ind_code: r.ind_code, c_code: r.c_code, years: {} };
                }
                if (!groupedDataOic[key]) {
                    groupedDataOic[key] = { ind_code: r.ind_code, c_code: r.c_code, years: {} };
                }
            
                groupedDataSdg[key].years[r.year] = r.sdg_value;
                groupedDataOic[key].years[r.year] = r.oic_value;
            });
        
            const allIndicators = [...new Set(Object.keys(groupedDataSdg).map(key => key.split('_')[0]))].sort((a, b) => a - b);
            const publicIndicators = allIndicators.filter(ind => indicators[ind]?.is_private !== 1);
            const privateIndicators = allIndicators.filter(ind => indicators[ind]?.is_private === 1);
            
            const currentYear = new Date().getFullYear();
            const allYears = [];
            for (let y = 2000; y <= currentYear; y++) allYears.push(y);
        
            const headers = ['ind_code', 'c_code', ...allYears];
            
            const generateSheetData = (indicatorList, groupedData) => {
                const sheetData = [headers];
                for (const ind of indicatorList) {
                    for (const c of allCountries) {
                        const key = `${ind}_${c}`;
                        const entry = groupedData[key];
                        const row = [ind, c];
                        allYears.forEach(y => {
                            const v = entry ? entry.years[y] : '';
                            row.push(v !== undefined && v !== null ? v : '');
                        });
                        sheetData.push(row);
                    }
                }
                return sheetData;
            };
            
            const exportExcel = (indicatorList, suffix) => {
                const wb = XLSX.utils.book_new();
                
                const sdgData = generateSheetData(indicatorList, groupedDataSdg);
                const wsSdg = XLSX.utils.aoa_to_sheet(sdgData);
                
                const oicData = generateSheetData(indicatorList, groupedDataOic);
                const wsOic = XLSX.utils.aoa_to_sheet(oicData);
                
                const unmatchedHeaders = ['Indicator Code', 'Indicator Name', 'Country Code', 'Country Name', 'Year', 'SDG Value', 'OIC Value'];
                const unmatchedData = [unmatchedHeaders];
                
                nonMatchedData.forEach(record => {
                    const indicator = indicators[record.ind_code];
                    const country = Object.values(countriesState.value || {}).find(c => c.c_code === record.c_code);
                    
                    unmatchedData.push([
                        record.ind_code,
                        indicator ? getIndicatorName(indicator) : record.ind_code,
                        record.c_code,
                        country ? country.c_name_eng : record.c_code,
                        record.year,
                        record.sdg_value,
                        record.oic_value
                    ]);
                });
                
                const wsUnmatched = XLSX.utils.aoa_to_sheet(unmatchedData);
                
                XLSX.utils.book_append_sheet(wb, wsSdg, 'SDG Values');
                XLSX.utils.book_append_sheet(wb, wsOic, 'OIC Values');
                XLSX.utils.book_append_sheet(wb, wsUnmatched, 'Unmatched Records');
                
                const dateStr = new Date().toISOString().split('T')[0];
                writeExcelFile(wb, `Goal_${goal}_${suffix}_${dateStr}.xlsx`);
            };
            
            if (publicIndicators.length > 0) {
                exportExcel(publicIndicators, 'public');
            }
            
            if (privateIndicators.length > 0) {
                exportExcel(privateIndicators, 'private');
            }
            
            message.success(`Exported ${nonMatchedData.length} non-matching records for ${publicIndicators.length} public and ${privateIndicators.length} private indicators to Excel`);
        
        } catch (error) {
            message.error('Failed to export Excel: ' + (error.response?.data?.detail || error.message));
        }
    };


    const fetchData = async (selectedGoal) => {
        try {
            setLoading(true);
            setProgress(0);
            setData([]);
        
            let sdgRecordsValue = 0;
            let progressInterval;
            
            try {
                const countResponse = await AxiosOICStat.get('/external_api/sdg/sdg-data-count');
                sdgRecordsValue = countResponse.data.sdg_records || 0;
                setSdgRecords(sdgRecordsValue);
                
                console.log(`SDG Records: ${sdgRecordsValue}`);
            } catch (countError) {
                console.warn('Could not get data count:', countError);
            }
        
            const maximumDuration = 10000000;
            const estimatedDuration = Math.min(maximumDuration);
        
            const updateInterval = 500;
            const totalUpdates = estimatedDuration / updateInterval;
            const progressIncrement = Math.max(1, Math.floor(95 / totalUpdates));
            
            let apiCompleted = false;
        
            progressInterval = setInterval(() => {
                setProgress(prev => {
                    if (apiCompleted) {
                        clearInterval(progressInterval);
                        return 100;
                    }
                    
                    const newProgress = Math.min(prev + progressIncrement, 95);
                    if (newProgress >= 95) {
                        return 95;
                    }
                    return newProgress;
                });
            }, updateInterval);
        
            const startTime = Date.now();
            const response = await AxiosOICStat.get(`/external_api/sdg/compare-all-sdg-with-all-oic?goal=${selectedGoal}`);
            const actualDuration = Date.now() - startTime;
            
            console.log(`Actual processing time: ${actualDuration}ms`);
        
            apiCompleted = true;
            clearInterval(progressInterval);
            setProgress(100);
        
            const transformedData = response.data.map((item, index) => ({
                ...item,
                key: index,
            }));
        
            setData(transformedData);
            setFilteredData(transformedData);
        
        } catch (error) {
            console.error('Error fetching SDG data:', error);
            
            let errorMessage = 'Failed to fetch SDG data';
            if (error.response?.status === 504) {
                errorMessage = 'Request timed out - the dataset might be too large. Please try again.';
            } else if (error.response?.status === 500) {
                errorMessage = 'Server error during processing. Please check the logs and try again.';
            } else if (error.response?.data?.detail) {
                errorMessage = `Error: ${error.response.data.detail}`;
            } else if (error.message) {
                errorMessage = `Error: ${error.message}`;
            }
            
            message.error(errorMessage, 10);
        } finally {
            setLoading(false);
            setTimeout(() => setProgress(0), 2000);
        }
    };

    const filterOptions = useMemo(() => {
        if (!data.length) return { indicators: [], countries: [], years: [], seriesCodes: [] };

        const indicators = [...new Set(data.map(item => item.ind_code))].sort((a, b) => a - b);
        const countries = [...new Set(data.map(item => item.c_code))].filter(Boolean).sort((a, b) => a - b);
        const years = [...new Set(data.map(item => item.year))].filter(Boolean).sort((a, b) => a - b);
        const seriesCodes = [...new Set(data.map(item => item.series_code))].filter(Boolean).sort();

        return {
            indicators,
            countries,
            years,
            seriesCodes
        };
    }, [data]);

    const applyFilters = (searchValue = searchText, currentFilters = filters) => {
        let filtered = [...data];

        if (currentFilters.indicator) {
            filtered = filtered.filter(record => record.ind_code === currentFilters.indicator);
        }

        if (currentFilters.country) {
            filtered = filtered.filter(record => record.c_code === currentFilters.country);
        }

        if (currentFilters.year) {
            filtered = filtered.filter(record => record.year === currentFilters.year);
        }

        if (currentFilters.match !== null) {
            filtered = filtered.filter(record => record.match === currentFilters.match);
        }

        if (currentFilters.seriesCode) {
            filtered = filtered.filter(record => record.series_code === currentFilters.seriesCode);
        }

        if (searchValue) {
            const searchLower = searchValue.toLowerCase();
            filtered = filtered.filter(record => {
                const indicator = indicatorsState.value ? indicatorsState.value[record.ind_code] : null;
                const indicatorName = indicator ? getIndicatorName(indicator) : '';
                
                return (
                    String(record.ind_code).toLowerCase().includes(searchLower) ||
                    indicatorName.toLowerCase().includes(searchLower) ||
                    String(record.c_code).toLowerCase().includes(searchLower) ||
                    (record.country_name || '').toLowerCase().includes(searchLower) ||
                    (record.series_code || '').toLowerCase().includes(searchLower) ||
                    (record.geo_area_code || '').toLowerCase().includes(searchLower)
                );
            });
        }

        setFilteredData(filtered);
    };

    const handleFilterChange = (filterKey, value) => {
        const newFilters = { ...filters, [filterKey]: value };
        setFilters(newFilters);
        applyFilters(searchText, newFilters);
    };

    const handleClearFilters = () => {
        setFilters({
            indicator: null,
            country: null,
            year: null,
            match: null,
            seriesCode: null,
        });
        setSearchText('');
        setFilteredData(data);
    };

    const handleSearch = (value) => {
        setSearchText(value);
        applyFilters(value, filters);
    };

    return (
        <div>
            <Card title="SDG Data" style={{ marginBottom: '16px' }}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <Button 
                            type="primary" 
                            onClick={() => setIsModalVisible(true)}
                            loading={loading}
                            size="large"
                        >
                            {loading ? 'Loading SDG Data...' : 'Load SDG Data'}
                        </Button>
                        
                        <Button 
                            type="default" 
                            icon={<DownloadOutlined />}
                            onClick={exportToCsv}
                            disabled={data.length === 0 || loading}
                            size="large"
                        >
                            Export to Excel
                        </Button>

                        {data.length > 0 && (
                            <Search
                                placeholder="Search by indicator code, name, country, series, or geo area"
                                allowClear
                                enterButton={<SearchOutlined />}
                                size="large"
                                onSearch={handleSearch}
                                onChange={(e) => handleSearch(e.target.value)}
                                value={searchText}
                                style={{ flex: 1, maxWidth: '600px' }}
                            />
                        )}
                    </div>

                    {data.length > 0 && (
                        <Card 
                            title={<><FilterOutlined /> Filters</>} 
                            size="small"
                            extra={
                                <Button 
                                    size="small" 
                                    icon={<ClearOutlined />}
                                    onClick={handleClearFilters}
                                >
                                    Clear All
                                </Button>
                            }
                        >
                            <Row gutter={[16, 16]}>
                                <Col xs={24} sm={12} md={8} lg={4}>
                                    <div>
                                        <div style={{ marginBottom: 4, fontSize: '12px', fontWeight: 500 }}>Indicator Code</div>
                                        <Select
                                            allowClear
                                            showSearch
                                            autoClearSearchValue={false}
                                            placeholder="All Indicators"
                                            style={{ width: '100%' }}
                                            value={filters.indicator}
                                            onChange={(value) => handleFilterChange('indicator', value)}
                                            filterOption={(input, option) => {
                                                const indicator = indicatorsState.value?.[option.value];
                                                const indicatorName = getIndicatorName(indicator) || '';
                                                return (
                                                    String(option.value).toLowerCase().includes(input.toLowerCase()) ||
                                                    indicatorName.toLowerCase().includes(input.toLowerCase())
                                                );
                                            }}
                                        >
                                            {filterOptions.indicators.map(ind => {
                                                const indicator = indicatorsState.value?.[ind];
                                                return (
                                                    <Select.Option key={ind} value={ind}>
                                                        {ind} {indicator ? `- ${getIndicatorName(indicator).substring(0, 30)}...` : ''}
                                                    </Select.Option>
                                                );
                                            })}
                                        </Select>
                                    </div>
                                </Col>

                                <Col xs={24} sm={12} md={8} lg={4}>
                                    <div>
                                        <div style={{ marginBottom: 4, fontSize: '12px', fontWeight: 500 }}>Country</div>
                                        <Select
                                            allowClear
                                            showSearch
                                            placeholder="All Countries"
                                            style={{ width: '100%' }}
                                            value={filters.country}
                                            onChange={(value) => handleFilterChange('country', value)}
                                            filterOption={(input, option) => {
                                                const country = Object.values(countriesState.value || {}).find(c => c.c_code === option.value);
                                                const countryName = country?.c_short_name_eng || '';
                                                return (
                                                    String(option.value).toLowerCase().includes(input.toLowerCase()) ||
                                                    countryName.toLowerCase().includes(input.toLowerCase())
                                                );
                                            }}
                                        >
                                            {filterOptions.countries.map(ccode => {
                                                const country = Object.values(countriesState.value || {}).find(c => c.c_code === ccode);
                                                return (
                                                    <Select.Option key={ccode} value={ccode}>
                                                        {ccode} - {country?.c_short_name_eng || 'Unknown'}
                                                    </Select.Option>
                                                );
                                            })}
                                        </Select>
                                    </div>
                                </Col>

                                <Col xs={24} sm={12} md={8} lg={4}>
                                    <div>
                                        <div style={{ marginBottom: 4, fontSize: '12px', fontWeight: 500 }}>Year</div>
                                        <Select
                                            allowClear
                                            showSearch
                                            placeholder="All Years"
                                            style={{ width: '100%' }}
                                            value={filters.year}
                                            onChange={(value) => handleFilterChange('year', value)}
                                        >
                                            {filterOptions.years.map(year => (
                                                <Select.Option key={year} value={year}>
                                                    {year}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </div>
                                </Col>

                                <Col xs={24} sm={12} md={8} lg={4}>
                                    <div>
                                        <div style={{ marginBottom: 4, fontSize: '12px', fontWeight: 500 }}>Match Status</div>
                                        <Select
                                            allowClear
                                            placeholder="All Status"
                                            style={{ width: '100%' }}
                                            value={filters.match}
                                            onChange={(value) => handleFilterChange('match', value)}
                                        >
                                            <Select.Option value={true}>Match</Select.Option>
                                            <Select.Option value={false}>No Match</Select.Option>
                                        </Select>
                                    </div>
                                </Col>

                                <Col xs={24} sm={12} md={8} lg={8}>
                                    <div>
                                        <div style={{ marginBottom: 4, fontSize: '12px', fontWeight: 500 }}>Series Code</div>
                                        <Select
                                            allowClear
                                            showSearch
                                            placeholder="All Series"
                                            style={{ width: '100%' }}
                                            value={filters.seriesCode}
                                            onChange={(value) => handleFilterChange('seriesCode', value)}
                                        >
                                            {filterOptions.seriesCodes.map(code => (
                                                <Select.Option key={code} value={code}>
                                                    {code}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </div>
                                </Col>
                            </Row>
                            
                            {(filters.indicator || filters.country || filters.year || filters.match !== null || filters.seriesCode) && (
                                <div style={{ marginTop: 12, fontSize: '12px', color: '#666' }}>
                                    Showing {filteredData.length} of {data.length} records
                                </div>
                            )}
                        </Card>
                    )}
                </Space>

                {loading && (
                    <div style={{ marginBottom: '16px' }}>
                        <Progress 
                            percent={progress} 
                            status={progress === 100 ? 'success' : 'active'}
                            strokeColor={{
                                '0%': '#108ee9',
                                '100%': '#87d068',
                            }}
                            showInfo={true}
                        />
                        <div style={{ textAlign: 'center', marginTop: '8px', color: '#666' }}>
                            Loading {sdgRecords > 0 ? `${sdgRecords.toLocaleString()} SDG records` : 'SDG data'}, please wait...
                        </div>
                    </div>
                )}

                {data.length > 0 && !loading && (
                    <Spin spinning={loading}>
                        <Table
                            columns={columns}
                            dataSource={filteredData}
                            rowKey="key"
                            pagination={{
                                pageSize: 50,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) => 
                                    `${range[0]}-${range[1]} of ${total} items`,
                                pageSizeOptions: ['20', '50', '100', '200']
                            }}
                            scroll={{ x: 'max-content' }}
                            size="small"
                            loading={loading}
                        />
                    </Spin>
                )}

                {data.length === 0 && !loading && (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px',
                        color: '#999',
                        border: '1px dashed #d9d9d9',
                        borderRadius: '6px'
                    }}>
                        No SDG data available. Click the button above to load data.
                    </div>
                )}
            </Card>

            <Modal
                title="Select Goal"
                visible={isModalVisible}
                onOk={() => {
                    setIsModalVisible(false);
                    fetchData(goal);
                }}
                onCancel={() => setIsModalVisible(false)}
            >
                <div>
                    <label>Goal:</label>
                    <Select
                        value={goal}
                        onChange={setGoal}
                        style={{ width: '100%', marginTop: '8px' }}
                    >
                        {Array.from({ length: 17 }, (_, i) => i + 1).map(num => (
                            <Select.Option key={num} value={num}>
                                {num}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            </Modal>
        </div>
    );
};